import { useState } from 'react';
import { Button, FlexContainer, InputMask, InputText, Typography } from '@uigovpe/components';
import { ErrorNotice } from '@/shared/components/ErrorNotice';
import { LabeledValue } from '@/shared/components/LabeledValue';
import { GovBrValue } from '@/shared/components/GovBrValue';
import { Profile } from '@/shared/types/certificate-request';
import { brDateToIso, formatDate, isoToBrDate } from '@/shared/utils/formatting';
import { PersonalDataInput } from '../../api/profileApi';

interface PersonalDataStepProps {
  profile: Profile;
  busy: boolean;
  error?: string | null;
  onSave: (input: PersonalDataInput) => void;
  onBack: () => void;
}

/**
 * Etapa 2. Só os campos que a CBMPE precisa para conferir a identidade no
 * contato manual.
 *
 * CPF e nome vêm do login e são exibidos como texto, nunca como input. Nome
 * da mãe e data de nascimento são digitados porque o gov.br não entrega
 * filiação nem nascimento — o `/userinfo` devolve nome, CPF, e-mail, telefone
 * e foto, e nada mais. Uma vez preenchidos, também travam: só nome da mãe
 * pode ser completado depois, e só se tiver ficado vazio.
 */
export function PersonalDataStep({
  profile,
  busy,
  error,
  onSave,
  onBack,
}: PersonalDataStepProps) {
  const [socialName, setSocialName] = useState(profile.socialName ?? '');
  const [motherName, setMotherName] = useState(profile.motherName ?? '');
  const [brBirthDate, setBrBirthDate] = useState(isoToBrDate(profile.birthDate));
  const [touched, setTouched] = useState(false);

  const motherNameLocked = Boolean(profile.motherName);
  const birthDateLocked = Boolean(profile.birthDate);

  const birthDate = brDateToIso(brBirthDate);
  const today = new Date().toISOString().slice(0, 10);

  const errors = {
    motherName: motherName.trim().length < 3 ? 'Informe o nome completo da sua mãe.' : '',
    birthDate: !birthDate
      ? 'Informe sua data de nascimento.'
      : birthDate >= today
        ? 'A data de nascimento precisa estar no passado.'
        : '',
  };
  const valid =
    (motherNameLocked || !errors.motherName) && (birthDateLocked || !errors.birthDate);
  const errorOf = (field: keyof typeof errors) => (touched ? errors[field] : '');

  return (
    <div className="cd-step-page">
      <section className="cd-step">
        <Typography variant="h2" size="lg" fontWeight="bold">
          Seus dados
        </Typography>
        <Typography variant="p" size="sm" className="cd-step__hint">
          Confirmamos sua identidade pelo login do portal. Faltam apenas os dados
          que o gov.br não fornece e que a certidão precisa.
        </Typography>

        <ErrorNotice message={error} />

        <FlexContainer direction="col" gap="6">
          <LabeledValue label="CPF" value={<GovBrValue value={profile.formattedCpf} />} />
          <LabeledValue label="Nome completo" value={<GovBrValue value={profile.name} />} />
          <InputText
            inputId="personal-social-name"
            label="Nome social"
            value={socialName}
            onChange={(e) => setSocialName(e.target.value)}
            suportText="Preencha se você usa um nome diferente do nome civil."
          />
          <div className="cd-grid cd-grid--2">
            {motherNameLocked ? (
              <LabeledValue label="Nome completo da mãe" value={profile.motherName} />
            ) : (
              <InputText
                inputId="personal-mother-name"
                label="Nome completo da mãe*"
                value={motherName}
                onChange={(e) => setMotherName(e.target.value)}
                className={errorOf('motherName') ? 'p-invalid' : ''}
                suportText={errorOf('motherName')}
              />
            )}
            {birthDateLocked ? (
              <LabeledValue label="Data de nascimento" value={formatDate(profile.birthDate)} />
            ) : (
              <InputMask
                id="personal-birth-date"
                label="Data de nascimento*"
                mask="99/99/9999"
                placeholder="DD/MM/AAAA"
                value={brBirthDate}
                onChange={(e) => setBrBirthDate(e.value ?? '')}
                className={errorOf('birthDate') ? 'p-invalid' : ''}
                suportText={errorOf('birthDate')}
              />
            )}
          </div>
        </FlexContainer>
      </section>

      <div className="cd-actions cd-actions--outside">
        <Button label="Voltar" outlined icon="pi pi-arrow-left" onClick={onBack} />
        <Button
          label="Continuar"
          icon="pi pi-arrow-right"
          iconPos="right"
          loading={busy}
          onClick={() => {
            setTouched(true);
            if (!valid) return;
            onSave({
              motherName: motherNameLocked ? undefined : motherName.trim(),
              birthDate: birthDateLocked ? undefined : (birthDate as string),
              socialName: socialName.trim() || undefined,
            });
          }}
        />
      </div>
    </div>
  );
}
